import { createMemo, createSignal, onCleanup, onMount } from "solid-js";
import { apiEndpoints, config } from "../../../../config/env";
import { streamingService } from "../../../../services/streamingService";
import { streamingStore } from "../../../../store/streamingStore";
import { streamingStatusStore } from "../../../../store/streamingStatusStore";
import { livePlaying } from "../../../../store/playbackStore";
import { sourcesStore } from "../../../../store/sourcesStore";
import { getData } from "../../../../utils/global";
import { arrowStreamClient } from "../../../../services/arrowStreamClient";
import { liveColumnarStore } from "../../../../store/liveColumnarStore";
import { isHydratingHistory } from '../../../../store/liveMapHydration';

/** gpuMode: live map runs on the columnar Arrow stream (streamingStore + legacy WS are idle). */
const GPU = config.LIVE_MAP_GPU === true;

type LiveStreamStatusVariant = "recent" | "delayed" | "paused" | "nodata";

interface LiveMapStreamStatusProps {
  selectedSourceIds: () => Set<number>;
  /** When set (live map), "Live" requires a recent map frame, not only a fresh store timestamp. */
  lastMapFrameAtMs?: () => number | null;
}

const RECENT_MAX_AGE_MS = 10_000;
/**
 * Cadence for the *configuration* half of this badge (is streaming started / simulating). The
 * second-by-second liveness half is driven locally by `clock()` and the streamingStore data
 * versions, so it needs no HTTP at all — this timer was polling status + sources + monitoring
 * every 5s for state that only changes when someone starts or stops a stream.
 */
const STREAM_STATUS_REFRESH_MS = 15_000;

interface DerivedStatus {
  variant: LiveStreamStatusVariant;
  label: string;
}

interface LiveStreamMonitoringStatus {
  streamingStarted: boolean;
  telemetry_backend?: 'timescale' | 'influx';
  influxdb?: {
    streaming?: boolean;
  };
  simulation?: {
    enabled?: boolean;
  };
}

/**
 * Newest sample for the selected boats.
 * In gpuMode the rings are keyed by the server's stream frame id, not the app source_id.
 * The map resolves that by boat name; this must do the same or the badge stays on
 * "Waiting for initialization" over a map that is already drawing.
 */
function liveEdgeMs(ids: Set<number>): number | null {
  if (!GPU) {
    const latest = streamingStore.getLatestTimestamp(ids);
    return latest != null && latest > 0 ? latest : null;
  }
  const names: string[] = [];
  for (const id of ids) {
    const name = sourcesStore.getSourceName(id);
    if (name) names.push(name);
  }
  const latest = names.length > 0
    ? liveColumnarStore.latestTsForNames(names)
    : liveColumnarStore.latestTsAny();
  return latest != null && latest > 0 ? latest : null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function parseMonitoringStatus(data: unknown): LiveStreamMonitoringStatus | null {
  if (!isRecord(data)) return null;

  const influxdb = isRecord(data.influxdb) ? data.influxdb : null;
  const simulation = isRecord(data.simulation) ? data.simulation : null;
  const backend = data.telemetry_backend;
  return {
    streamingStarted: data.streamingStarted === true,
    telemetry_backend: backend === 'timescale' || backend === 'influx' ? backend : undefined,
    influxdb: influxdb
      ? {
          streaming: typeof influxdb.streaming === "boolean" ? influxdb.streaming : undefined,
        }
      : undefined,
    simulation: simulation
      ? {
          enabled: typeof simulation.enabled === "boolean" ? simulation.enabled : undefined,
        }
      : undefined,
  };
}

/**
 * Lower-left live map indicator: connection / freshness vs playhead.
 */
export default function LiveMapStreamStatus(props: LiveMapStreamStatusProps) {
  const [wsConnected, setWsConnected] = createSignal(streamingService.isConnected());
  const [monitoringStatus, setMonitoringStatus] = createSignal<LiveStreamMonitoringStatus | null>(null);
  const [clock, setClock] = createSignal(0);

  onMount(() => {
    const off = streamingService.onConnection((connected) => {
      setWsConnected(connected);
    });
    setWsConnected(streamingService.isConnected());

    const refreshStreamStatus = () => {
      // Stale-gated: the store runs its own background timer, and other live pages refresh it too.
      streamingStatusStore.refreshIfStale(STREAM_STATUS_REFRESH_MS - 1_000).catch(() => {});
      getData(apiEndpoints.stream.monitoringStatus)
        .then((response: unknown) => {
          if (!isRecord(response) || response.success !== true) return;
          setMonitoringStatus(parseMonitoringStatus(response.data));
        })
        .catch(() => {});
    };

    refreshStreamStatus();
    const id = window.setInterval(() => setClock((c) => c + 1), 1000);
    const statusId = window.setInterval(() => {
      refreshStreamStatus();
    }, STREAM_STATUS_REFRESH_MS);
    onCleanup(() => {
      off();
      window.clearInterval(id);
      window.clearInterval(statusId);
    });
  });

  const status = createMemo((): DerivedStatus => {
    void clock();
    streamingStore.getLiveDataAppendVersion()();
    streamingStore.getHistoricalDataVersion()();
    streamingStore.error()();
    streamingStore.loading()();
    if (GPU) void liveColumnarStore.epoch();

    const streamingStatus = streamingStatusStore.status()();
    const monitoring = monitoringStatus();
    const simulationActive =
      streamingStatus.simulationActive || monitoring?.simulation?.enabled === true;
    const ingestionActive =
      streamingStatus.isStreamingActive ||
      streamingStatus.streamingStarted ||
      monitoring?.streamingStarted === true ||
      simulationActive;

    const telemetryDown =
      !streamingStatus.influxConnected ||
      (!simulationActive && monitoring?.streamingStarted === true && monitoring.influxdb?.streaming === false);
    const telemetryDownLabel =
      monitoring?.telemetry_backend === 'timescale' ? 'Timescale down' : 'Influx down';

    if (!config.ENABLE_WEBSOCKETS) {
      return { variant: "nodata", label: "Streaming off" };
    }

    // Connection: the arrow WS in gpuMode (the legacy JSON WS is retired there), else streamingService.
    const connected = GPU ? arrowStreamClient.isConnected() : wsConnected();
    if (!connected) {
      if (GPU || streamingStore.loading()()) {
        return { variant: "delayed", label: "Connecting…" };
      }
      return { variant: "nodata", label: "Offline" };
    }

    const ids = props.selectedSourceIds();
    if (ids.size === 0) {
      return { variant: "nodata", label: "No sources" };
    }

    const latest = liveEdgeMs(ids);
    if (latest === null) {
      if (telemetryDown) {
        return { variant: "nodata", label: telemetryDownLabel };
      }
      if (GPU || streamingStore.loading()() || ingestionActive) {
        return { variant: "delayed", label: "Waiting for initialization…" };
      }
      return { variant: "nodata", label: "No data" };
    }

    // `livePlaying()` (which IS `liveFollow`), not `isPlaying`.
    //
    // `isPlaying` is the clock, and the clock is machinery: teardown, a zoom/pan hold, a chart
    // reinitialising and a backfill all stop it without the user touching anything. Reading it here
    // made this badge say "Paused" over a map the user had not paused — and worse, it said it for a
    // STALLED FEED, which is the one condition this badge exists to surface. Follow is the half only
    // a gesture writes, so it is the only honest answer to "did someone pause this?".
    if (!livePlaying()) {
      return {
        variant: "paused",
        label: isHydratingHistory() ? "Paused · backfilling…" : "Paused",
      };
    }

    const ageMs = Date.now() - latest;
    const frameAt = props.lastMapFrameAtMs?.();
    const mapFrameAgeMs =
      frameAt === undefined
        ? 0
        : frameAt === null
          ? Number.POSITIVE_INFINITY
          : Date.now() - frameAt;
    const dataRecent = ageMs <= RECENT_MAX_AGE_MS;
    const mapRecent = mapFrameAgeMs <= RECENT_MAX_AGE_MS;

    // Backfilling is reported ALONGSIDE the live state, not instead of it. Widening the time window
    // sends the map to parquet for history it never streamed, and during that fetch the feed is
    // still perfectly live — saying only "Backfilling" would read as an interruption, and saying
    // only "Live" leaves a widened window looking broken while the older track is still on its way.
    const backfilling = isHydratingHistory();

    if (dataRecent && mapRecent) {
      return {
        variant: "recent",
        label: backfilling ? "Live · backfilling…" : "Live",
      };
    }
    return { variant: "delayed", label: backfilling ? "Delayed · backfilling…" : "Delayed" };
  });

  const modifierClass = createMemo(() => {
    const v = status().variant;
    return `live-map-stream-status--${v}`;
  });

  return (
    <div class={`live-map-stream-status ${modifierClass()}`} role="status" aria-live="polite">
      <span class="live-map-stream-status__dot" aria-hidden="true" />
      <span class="live-map-stream-status__label">{status().label}</span>
    </div>
  );
}
